import {t,onLanguageChange,localeState} from './i18n.js';
// Text offsets stay relative to published blocks, so marks neither change audio
// timing coordinates nor depend on font size, line wrapping, or reader mode.
const colors = ['honey', 'sage', 'lavender'];
export class ReaderAnnotations {
  constructor(engine, items, persist) {
    this.engine=engine;
    this.root=engine.shadowRoot;
    this.items=items;
    this.persist=persist;
    this.messages=()=>({...localeState().messages,...Object.fromEntries(colors.map(color=>['highlight-'+color,t('annotation-color-highlight',{color:({honey:t('ui-honey'),sage:t('ui-sage'),lavender:t('ui-lavender')})[color]})]))});
    this.abort=new AbortController();
    const options={signal:this.abort.signal};
    this.panel=document.createElement('div');
    this.panel.className='selection-tools';
    this.panel.setAttribute('popover','manual');
    this.panel.setAttribute('role','region');
    this.panel.setAttribute('aria-label',t("ui-passage-actions"));
    this.root.append(this.panel);
    const node=document.createElement('div');this.panel.append(node);
    this.app=window.Elm.Annotations.init({node,flags:{messages:this.messages()}});
    this.request=({tag,data})=>this.effect(tag,data).catch(error=>{this.send('failed');this.engine.emit('error',error.message);});this.app.ports.annotationRequest.subscribe(this.request);
    this.send('items',items);
    const localize=()=>{this.send('locale',this.messages());this.panel.lang=localeState().language;this.panel.setAttribute('aria-label',t('ui-passage-actions'));for(const mark of this.root.querySelectorAll('mark[data-highlight-id][tabindex]')){const item=this.items.find(item=>item.id===mark.dataset.highlightId);if(item)mark.setAttribute('aria-label',t('ui-edit-highlight',{value1:item.excerpt.slice(0,100)}));}};
    localize();this.localeSubscription=onLanguageChange(localize);
    this.panel.addEventListener('pointerdown',e=>{
      // Keep the native selection while pressing an action; inputs still focus.
      if(e.target.closest('button'))e.preventDefault();
    },options);
    this.panel.addEventListener('keydown',e=>{
      e.stopPropagation();
      if(e.key==='Escape'){e.preventDefault();this.dismiss();}
    },options);
    this.root.addEventListener('pointerup',()=>this.schedule(),options);
    this.root.addEventListener('touchend',()=>this.schedule(),options);
    document.addEventListener('selectionchange',()=>this.schedule(),options);
    document.addEventListener('pointerdown',e=>{
      if(!e.composedPath().includes(this.panel)&&!e.composedPath().some(el=>el?.dataset?.highlightId))this.hide();
    },options);
    document.addEventListener('keydown',e=>{
      if(e.key==='Escape'&&this.isOpen()){e.preventDefault();e.stopImmediatePropagation();this.dismiss();}
    },{...options,capture:true});
    const move=()=>{
      if(this.isOpen()&&this.panel.querySelector('textarea')&&this.panel.contains(this.root.activeElement))this.place();
      else{const pending=!!this.timer;this.hide();if(pending)this.schedule();}
    };
    window.addEventListener('scroll',move,{...options,passive:true});
    window.addEventListener('resize',move,options);
    window.visualViewport?.addEventListener('resize',move,options);
    this.root.addEventListener('click',e=>{
      const mark=e.composedPath().find(el=>el?.dataset?.highlightId);
      if(!mark||engine.pickingAudio||this.nativeSelection()?.toString().trim())return;
      e.preventDefault();e.stopPropagation();
      const item=this.items.find(item=>item.id===mark.dataset.highlightId);
      if(item){this.anchor=mark.getBoundingClientRect();this.send('capture',item);}
    },{...options,capture:true});
    this.root.addEventListener('keydown',e=>{
      if(e.target?.dataset?.highlightId&&['Enter',' '].includes(e.key)){
        e.preventDefault();e.stopPropagation();this.nativeSelection()?.removeAllRanges();e.target.click();this.panel.querySelector('button')?.focus({preventScroll:true});
      }
    },options);
    this.paint();
  }

  destroy(){this.app.ports.annotationRequest.unsubscribe(this.request);this.localeSubscription?.();this.destroyed=true;clearTimeout(this.timer);this.abort.abort();this.panel.remove();}
  setItems(items){this.items=items;this.send('items',items);this.paint();}
  isOpen(){return this.panel.matches(':popover-open');}
  nativeSelection(){return this.root.getSelection?.()||window.getSelection();}
  hide(){
    this.activeId=null;this.send('hidden');
    clearTimeout(this.timer);this.timer=0;
    if(this.isOpen()){
      if(this.panel.contains(this.root.activeElement))this.root.activeElement.blur();
      this.panel.hidePopover();
    }
  }
  dismiss(){this.hide();this.nativeSelection()?.removeAllRanges();this.engine.emit('selection','');}
  schedule(){clearTimeout(this.timer);this.timer=setTimeout(()=>{this.timer=0;this.select();},180);}

  selectedRange(){
    const selection=this.nativeSelection();
    if(!selection||selection.isCollapsed)return null;
    let range=selection.rangeCount?selection.getRangeAt(0).cloneRange():null;
    if(!range||range.startContainer.getRootNode()!==this.root){
      const composed=selection.getComposedRanges?.({shadowRoots:[this.root]})?.[0];
      if(composed){range=document.createRange();range.setStart(composed.startContainer,composed.startOffset);range.setEnd(composed.endContainer,composed.endOffset);}
    }
    return range&&range.startContainer.getRootNode()===this.root&&range.endContainer.getRootNode()===this.root?range:null;
  }

  select(){
    if(this.engine.pickingAudio||this.panel.contains(this.root.activeElement)||document.querySelector('.panel-overlay'))return;
    const range=this.selectedRange();
    if(!range||!range.toString().trim()){if(!this.activeId)this.hide();return;}
    if(this.panel.contains(range.startContainer)||this.panel.contains(range.endContainer))return;
    const segments=[];
    const walker=document.createTreeWalker(this.engine.flow,NodeFilter.SHOW_TEXT);
    let node;
    while((node=walker.nextNode())){
      if(!node.length||!range.intersectsNode(node))continue;
      const block=node.parentElement.closest('[id]');
      if(!block?.closest('.chapter-body')||node.parentElement.closest('button,.book-video-shell,script,style'))continue;
      const start=node===range.startContainer?range.startOffset:0;
      const end=node===range.endContainer?range.endOffset:node.length;
      if(start>=end)continue;
      const prefix=document.createRange();prefix.selectNodeContents(block);prefix.setEnd(node,0);
      const offset=prefix.toString().length;
      const chapter=block.closest('.book-chapter');
      segments.push({chapterId:chapter.id,block:block.id,start:offset+start,end:offset+end,quote:node.data.slice(start,end)});
    }
    if(!segments.length){this.hide();return;}
    const rects=[...range.getClientRects()].filter(r=>r.width&&r.height&&r.bottom>0&&r.top<innerHeight);
    this.anchor=rects.at(-1)||range.getBoundingClientRect();
    this.send('selection',{segments,id:crypto.randomUUID(),created:new Date().toISOString(),title:this.engine.book.title,chapters:Object.fromEntries(this.engine.book.chapters.map(chapter=>[chapter.id,chapter.title]))});
  }

  send(tag,data=null){if(!this.destroyed)this.app?.ports.annotationResponse.send({tag,data});}
  async effect(tag,data){
    switch(tag){
      case'show':this.activeId=data||null;requestAnimationFrame(()=>{if(!this.destroyed){this.panel.showPopover();this.place();}});return;
      case'place':requestAnimationFrame(()=>{if(!this.destroyed){this.place();if(data)this.panel.querySelector('textarea')?.focus({preventScroll:true});}});return;
      case'dismiss':this.dismiss();return;
      case'save':{
        try{await this.persist(data.items);if(this.destroyed)return;this.items=data.items;this.paint();this.send('saved',data);this.engine.emit('toast',t(data.message));}
        catch(error){if(!this.destroyed){this.send('failed');this.engine.emit('error',t('ui-could-not-save-this-passage',{value1:error.message}));}}return;
      }
      case'copy':{
        try{await navigator.clipboard.writeText(data);if(!this.destroyed){this.dismiss();this.engine.emit('toast',t('ui-passage-copied'));}}
        catch{if(!this.destroyed)this.engine.emit('toast',t('ui-use-your-browsers-copy-action-for-this-selection'));}return;
      }
    }
  }

  place(){
    const viewport=window.visualViewport;
    const left=viewport?.offsetLeft||0,top=viewport?.offsetTop||0;
    const width=viewport?.width||innerWidth,height=viewport?.height||innerHeight;
    this.panel.style.maxWidth=`${Math.max(0,width-16)}px`;
    const rect=this.panel.getBoundingClientRect();
    const x=Math.max(left+8,Math.min(left+width-rect.width-8,(this.anchor.left+this.anchor.right-rect.width)/2));
    let y=this.anchor.bottom+12;
    if(y+rect.height>top+height-12)y=this.anchor.top-rect.height-12;
    y=Math.max(top+8,Math.min(top+height-rect.height-8,y));
    this.panel.style.left=`${x}px`;this.panel.style.top=`${y}px`;
  }

  paint(scope=this.root){
    for(const mark of scope.querySelectorAll('mark[data-highlight-id]'))mark.replaceWith(...mark.childNodes);
    const blocks=new Map();
    for(const item of this.items){
      if(!item?.id||typeof item.excerpt!=='string'||!colors.includes(item.color)||!Array.isArray(item.segments))continue;
      for(const segment of item.segments){
        if(!segment||typeof segment.chapterId!=='string'||typeof segment.block!=='string')continue;
        const index=this.engine.chapterIds?.get(segment.chapterId);
        const chapter=Number.isInteger(index)?this.engine.flow?.children[index]:this.engine.flow?.querySelector(`#${CSS.escape(segment.chapterId)}`);
        const block=chapter?.querySelector(`#${CSS.escape(segment.block)}`);
        // Never put a highlight over different text after an edition update.
        if(!block||!scope.contains(block)||!Number.isInteger(segment.start)||!Number.isInteger(segment.end)||segment.start<0||segment.end<=segment.start||block.textContent.slice(segment.start,segment.end)!==segment.quote)continue;
        if(!blocks.has(block))blocks.set(block,[]);
        blocks.get(block).push({...segment,item});
      }
    }
    const focusable=new Set([...this.root.querySelectorAll('mark[data-highlight-id][tabindex]')].map(mark=>mark.dataset.highlightId));
    for(const [block,segments] of blocks){
      const walker=document.createTreeWalker(block,NodeFilter.SHOW_TEXT);
      const nodes=[];let offset=0,node;
      while((node=walker.nextNode())){nodes.push({node,start:offset,end:offset+node.length});offset+=node.length;}
      for(const {node,start,end} of nodes){
        const hits=segments.filter(s=>s.start<end&&s.end>start);
        if(!hits.length)continue;
        const edges=[...new Set([start,end,...hits.flatMap(s=>[Math.max(start,s.start),Math.min(end,s.end)])])].sort((a,b)=>a-b);
        const fragment=document.createDocumentFragment();
        for(let i=0;i<edges.length-1;i++){
          const a=edges[i],b=edges[i+1],hit=hits.findLast(s=>s.start<=a&&s.end>=b);
          const text=document.createTextNode(node.data.slice(a-start,b-start));
          if(hit){const mark=document.createElement('mark');mark.dataset.highlightId=hit.item.id;mark.dataset.color=hit.item.color;if(!focusable.has(hit.item.id)){mark.tabIndex=0;mark.setAttribute('role','button');mark.setAttribute('aria-label',t("ui-edit-highlight", {value1:(hit.item.excerpt.slice(0,100))}));focusable.add(hit.item.id);}if(hit.item.note){mark.className='has-note';mark.title=hit.item.note;}mark.append(text);fragment.append(mark);}
          else fragment.append(text);
        }
        node.replaceWith(fragment);
      }
    }
  }
}
